import { Handle, Position, type NodeProps } from '@xyflow/react';
import { ChevronDown, Crosshair, House, MapPin, Minus, UserPlus } from 'lucide-react';
import { memo } from 'react';
import { colorKeyFor, flagFor, swatchFor, tintOf } from '../lib/colors';
import { matchesFilters } from '../lib/filters';
import { resolveTheme } from '../lib/theme';
import { CARD_HEIGHT, CARD_WIDTH } from '../lib/layout';
import { cn, departmentColor, isRemote, placeLabel } from '../lib/utils';
import { useAppStore } from '../store/useAppStore';
import { Avatar } from './Avatar';

/** How far the expand pill hangs below the card, and so where a line may start. */
const PILL_CLEARANCE = 15;

export interface PersonNodeData extends Record<string, unknown> {
  personId: string;
}

/**
 * A single org-chart card. It reads straight from the store rather than taking
 * props through React Flow, so highlighting or selecting a person re-renders
 * one card instead of rebuilding the whole node array.
 */
function PersonNodeInner({ data, dragging }: NodeProps & { data: PersonNodeData }) {
  const id = data.personId;

  const person = useAppStore((s) => s.model?.byId.get(id));
  const counts = useAppStore((s) => s.model?.counts.get(id));
  const isCollapsed = useAppStore((s) => s.collapsedIds.includes(id));
  const isMoved = useAppStore((s) => s.movedIds.has(id));
  const isDeparting = useAppStore((s) => s.model?.departures.has(id) ?? false);
  const isSelected = useAppStore((s) => s.selectedId === id);
  const isPulsing = useAppStore((s) => s.pulseId === id);
  const isFocusRoot = useAppStore((s) => s.focusId === id);
  const dropTargetId = useAppStore((s) => s.drop.targetId);
  const dropOk = useAppStore((s) => s.drop.ok);
  const draggingId = useAppStore((s) => s.draggingId);
  const dark = useAppStore((s) => resolveTheme(s.theme) === 'dark');
  // Kept only to connect a match to the top: real, but not what was asked for.
  const isContext = useAppStore((s) => s.filtered.keep !== null && !matchesFilters(s, id));
  /*
   * The bucket this card is coloured by, if any. A team is the case that
   * matters: its people are scattered across the chart on purpose, and a shared
   * colour is the only thing that gathers them.
   */
  const colorKey = useAppStore((s) => {
    const target = s.model?.byId.get(id);
    return target ? colorKeyFor(target, s.colorBy, s.planSquads) : null;
  });
  const overrides = useAppStore((s) => s.colorOverrides);
  const colorBy = useAppStore((s) => s.colorBy);

  const toggleCollapse = useAppStore((s) => s.toggleCollapse);
  const select = useAppStore((s) => s.select);
  const openPanel = useAppStore((s) => s.openPanel);
  const setFocus = useAppStore((s) => s.setFocus);
  const setHireUnder = useAppStore((s) => s.setHireUnder);

  if (!person) return null;

  const directReports = counts?.directReports ?? 0;
  const totalReports = counts?.totalReports ?? 0;
  const hasChildren = directReports > 0;
  const isDropTarget = dropTargetId === id && draggingId !== null;
  // The avatar always says which department, whatever the cards are coloured
  // by; the bar down the edge is what carries the colouring.
  const dept = departmentColor(person.department, dark);
  const accent = colorKey ? swatchFor(colorKey, overrides, colorBy) : null;
  const place = placeLabel(person.location, person.country);
  const flag = flagFor(person.country);
  const remote = isRemote(person.location);

  /*
   * What the band along the bottom says: the bucket the card is coloured by,
   * or the department when colouring is off — which is otherwise the one thing
   * a plain card stops saying at all. The department keeps its own colour, the
   * same one the avatar is wearing, on a neutral band.
   */
  const band =
    colorKey && accent
      ? { label: colorKey, tint: tintOf(accent, 26), ink: null }
      : person.department
        ? { label: person.department, tint: 'var(--color-surface-2)', ink: dept.fg }
        : null;

  return (
    <div
      style={{ width: CARD_WIDTH, height: CARD_HEIGHT }}
      className={cn('group relative', dragging && 'cursor-grabbing')}
    >
      {/* Three targets, because a report is joined to its manager from above
          when it sits under them and from the side when it hangs off a trunk. */}
      <Handle type="target" position={Position.Top} isConnectable={false} />
      <Handle id="left" type="target" position={Position.Left} isConnectable={false} />
      <Handle id="right" type="target" position={Position.Right} isConnectable={false} />

      {/*
        Sides, for a dotted line. It joins two cards that can be anywhere
        relative to each other, so it leaves from whichever side faces the
        other one — out of the bottom it would loop back on itself half the
        time. Reporting lines never use these.
      */}
      <Handle id="dotted-left" type="source" position={Position.Left} isConnectable={false} />
      <Handle id="dotted-right" type="source" position={Position.Right} isConnectable={false} />
      {/*
       * Dropped below the expand pill, which straddles the card's bottom edge.
       * A line starting at the edge runs behind the pill and comes out of its
       * sides, which read as two stray stubs rather than one connector.
       *
       * Named, and every reporting edge asks for it by name. React Flow falls
       * back to the *first* source handle it finds on the node when an edge
       * does not say — so the moment the two dotted handles above were added,
       * every reporting line started leaving from the middle of the card's
       * left edge instead. The bracket then turned inside the card, and the
       * only part of it not hidden behind the card was a stub poking out of
       * the left side.
       */}
      <Handle
        id="reports"
        type="source"
        position={Position.Bottom}
        isConnectable={false}
        style={{ bottom: -PILL_CLEARANCE }}
      />

      <div
        onClick={() => {
          select(id);
          openPanel('details');
        }}
        /*
         * A wash over the whole card and a band along the bottom naming the
         * bucket — the shape ChartHop uses, and it gathers a scattered team from
         * further away than a bar down the edge did.
         *
         * `color-mix` rather than a precomputed rgba, because the tint has to sit
         * over `--color-surface` and that is a different colour in each theme.
         *
         * Eight percent, and faint on purpose. Red and orange are in this palette
         * now — Mexico and Norway, interns — and they used to be safe because the
         * colour lived in a thin bar. A card whose *background* goes red must
         * still never be mistakable for the ring that means somebody moved, so
         * the tint stays a long way from the ring, the dashes and the badges that
         * carry those states.
         */
        style={accent ? { background: tintOf(accent, 8) } : undefined}
        className={cn(
          'flex h-full flex-col overflow-hidden rounded-xl border bg-[var(--color-surface)] text-left',
          'shadow-[var(--shadow-card)] transition-[box-shadow,border-color,opacity,transform] duration-150',
          'hover:shadow-[var(--shadow-card-hover)]',
          'border-[var(--color-line)]',
          isMoved && 'border-[var(--color-moved)] ring-2 ring-[var(--color-moved)]/70',
          person.isNew && 'border-dashed border-[var(--color-fresh)] ring-2 ring-[var(--color-fresh)]/40',
          isDeparting && 'border-dashed border-[var(--color-leaving)]',
          isSelected && !isMoved && 'border-[var(--color-brand)] ring-2 ring-[var(--color-brand)]/50',
          isDropTarget &&
            dropOk &&
            'ring-4 ring-[var(--color-fresh)] ring-offset-2 ring-offset-[var(--color-canvas)]',
          isDropTarget &&
            !dropOk &&
            'ring-4 ring-[var(--color-moved)] ring-offset-2 ring-offset-[var(--color-canvas)]',
          dragging && 'rotate-[0.6deg] scale-[1.02] shadow-[var(--shadow-panel)]',
          isPulsing && 'pulse-ring',
          isContext && 'opacity-60 shadow-none',
        )}
      >
        {/* Padded, so the band below can run edge to edge. */}
        <div className="flex min-h-0 flex-1 flex-col justify-between px-3 pt-2.5 pb-1.5">
          <div className="flex items-start gap-2.5">
            <Avatar person={person} size={32} colors={dept} className="mt-0.5" />

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <p
                  className={cn(
                    'truncate text-[13px] leading-tight font-semibold text-[var(--color-ink)]',
                    isDeparting && 'line-through decoration-[var(--color-leaving)] decoration-2',
                  )}
                  title={person.name}
                >
                  {person.name}
                </p>
                {person.status === 'Accepted' ? (
                  <span
                    title="Offer accepted — has not started yet"
                    className="size-1.5 shrink-0 rounded-full bg-[var(--color-brand-vivid)]"
                  />
                ) : null}

                {/*
                  On the name's line, and pushed to the corner. It used to share
                  the row that now holds the counts, where it was competing with
                  the department for one slot; up here nothing has to give way to
                  it, and a card that changed says so at the top.
                */}
                <div className="ml-auto flex shrink-0 items-center gap-1">
                  {isMoved ? (
                    <span className="text-[9px] font-bold tracking-wide text-[var(--color-moved)]">
                      MOVED
                    </span>
                  ) : null}
                  {person.isNew ? (
                    <span className="text-[9px] font-bold tracking-wide text-[var(--color-fresh)]">NEW</span>
                  ) : null}
                  {isDeparting ? (
                    <span className="text-[9px] font-bold tracking-wide text-[var(--color-leaving)]">
                      LEAVING
                    </span>
                  ) : null}
                </div>
              </div>
              <p
                className="mt-0.5 line-clamp-2 text-[11px] leading-[1.25] text-[var(--color-ink-2)]"
                title={person.title}
              >
                {person.title}
              </p>
            </div>
          </div>

          {/*
            Where they work and how many report to them, on one line — the two
            things asked of a card after the title, and neither is ever long
            enough to need a row of its own. A row each left a hole down the
            middle of the card and cost 24px of height on every one of them.
          */}
          <div className="flex items-center justify-between gap-2 text-[10.5px] text-[var(--color-ink-3)]">
            <div className="flex min-w-0 items-center gap-1">
              {remote ? <House size={10} className="shrink-0" /> : <MapPin size={10} className="shrink-0" />}
              {/* The flag reads at a glance where the country name would not fit,
                  and it is the country rather than the office -- somebody remote in
                  Norway is still in Norway. */}
              {flag ? (
                <span className="shrink-0" aria-hidden title={person.country ?? undefined}>
                  {flag}
                </span>
              ) : null}
              <span className={cn('truncate', remote && 'italic')} title={place}>
                {place}
              </span>
            </div>

            <div className="flex shrink-0 items-center gap-1 font-medium">
              {hasChildren ? (
                <>
                  <span className="text-[var(--color-ink-2)]">{directReports}</span> direct
                  <span className="opacity-40">·</span>
                  <span className="text-[var(--color-ink-2)]">{totalReports}</span> total
                </>
              ) : (
                <span className="opacity-70">Individual contributor</span>
              )}
            </div>
          </div>
        </div>

        {/*
          The band, and what it says. Left-aligned and kept narrow because the
          expand pill straddles this edge in the middle of the card; a centred
          label would sit underneath it.

          The label is ink rather than white-on-colour: the band is mostly
          surface, so it reads at 10px whatever the hue is — where white on a
          100% Tive Green or amber would not.
        */}
        {band ? (
          <div style={{ background: band.tint }} className="flex shrink-0 items-center px-3 py-1">
            <span
              title={band.label}
              style={band.ink ? { color: band.ink } : undefined}
              className={cn(
                'max-w-[6.5rem] truncate text-[10px] font-semibold',
                !band.ink && 'text-[var(--color-ink-2)]',
              )}
            >
              {band.label}
            </span>
          </div>
        ) : null}
      </div>

      {/* Hover actions: focus this subtree, add a report */}
      <div
        className={cn(
          'absolute -top-2.5 right-1.5 flex items-center gap-1 rounded-lg border border-[var(--color-line)]',
          'bg-[var(--color-surface)] p-0.5 opacity-0 shadow-[var(--shadow-card)] transition-opacity',
          'group-hover:opacity-100 focus-within:opacity-100',
          draggingId !== null && 'hidden',
        )}
      >
        <button
          type="button"
          title={isFocusRoot ? 'Already focused here' : `Focus on ${person.name}'s team`}
          onClick={(event) => {
            event.stopPropagation();
            setFocus(isFocusRoot ? null : id);
          }}
          className={cn(
            'flex size-6 items-center justify-center rounded-md text-[var(--color-ink-3)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-brand)]',
            isFocusRoot && 'text-[var(--color-brand)]',
          )}
        >
          <Crosshair size={12} />
        </button>
        <button
          type="button"
          title={`Add a new role reporting to ${person.name}`}
          onClick={(event) => {
            event.stopPropagation();
            setHireUnder(id);
          }}
          className="flex size-6 items-center justify-center rounded-md text-[var(--color-ink-3)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-fresh)]"
        >
          <UserPlus size={12} />
        </button>
      </div>

      {/* Expand / collapse pill, straddling the bottom edge like Rippling's chart */}
      {hasChildren ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            toggleCollapse(id);
          }}
          title={isCollapsed ? `Show ${totalReports} below ${person.name}` : `Hide ${person.name}'s reports`}
          className={cn(
            'absolute -bottom-3 left-1/2 z-10 flex h-6 -translate-x-1/2 items-center gap-1 rounded-full',
            'border border-[var(--color-line)] bg-[var(--color-surface)] px-2 text-[10.5px] font-semibold',
            'text-[var(--color-ink-2)] shadow-[var(--shadow-card)] transition-all',
            'hover:border-[var(--color-brand)] hover:text-[var(--color-brand)]',
            isCollapsed && 'border-[var(--color-brand)]/40 text-[var(--color-brand)]',
          )}
        >
          {isCollapsed ? (
            <>
              <ChevronDown size={11} />
              {totalReports}
            </>
          ) : (
            <Minus size={11} />
          )}
        </button>
      ) : null}
    </div>
  );
}

export const PersonNode = memo(PersonNodeInner);
